import React, { useState, useEffect } from 'react';
import { 
    Users, Database, HardDrive, ShieldCheck, 
    Search, Check, X, UserPlus
} from 'lucide-react';
import axios from 'axios';
import Layout from '../components/Layout';
import RegisterModal from '../components/RegisterModal';
import { useAuth } from '../context/AuthContext';

const AdminDashboard = () => {
    const { token } = useAuth();
    const [stats, setStats] = useState(null);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true); // eslint-disable-line no-unused-vars
    const [searchTerm, setSearchTerm] = useState('');
    const [isRegisterModalOpen, setIsRegisterModalOpen] = useState(false);

    const fetchData = React.useCallback(async () => {
        try {
            const config = { headers: { Authorization: `Bearer ${token}` } };
            const statsRes = await axios.get('http://localhost:4000/api/admin/stats', config);
            const usersRes = await axios.get('http://localhost:4000/api/admin/users', config);
            
            setStats(statsRes.data.data);
            setUsers(usersRes.data.data);
        } catch (error) {
            console.error("Admin Fetch Error", error);
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleRoleUpdate = async (userId, newRole) => {
        try {
            await axios.put('http://localhost:4000/api/admin/users/role', 
                { userId, role: newRole },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            // Refresh
            const usersRes = await axios.get('http://localhost:4000/api/admin/users', { headers: { Authorization: `Bearer ${token}` } });
            setUsers(usersRes.data.data);
        } catch (error) {
             console.error("Role Update Error", error);
        }
    };

    const filteredUsers = users.filter(u => 
        u.email.toLowerCase().includes(searchTerm.toLowerCase()) || 
        (u.first_name && u.first_name.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    return (
        <Layout>
            {loading ? (
                <div className="flex h-[80vh] items-center justify-center">
                     <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                </div>
            ) : (
                <div className="space-y-8">
                {/* Header */}
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Admin Dashboard</h1>
                        <p className="text-gray-500 mt-2">System-wide monitoring and user management.</p>
                    </div>
                    <button 
                        onClick={() => setIsRegisterModalOpen(true)}
                        className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition shadow-lg shadow-blue-500/20"
                    >
                        <UserPlus className="w-5 h-5 mr-2" />
                        Register New User
                    </button>
                </div>

                {/* Stats Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    <StatCard 
                        title="Total Users" 
                        value={stats?.userCount || 0} 
                        icon={<Users className="w-6 h-6 text-white" />}
                        color="bg-purple-600"
                    />
                     <StatCard 
                        title="Total Households" 
                        value={stats?.householdCount || 0} 
                        icon={<ShieldCheck className="w-6 h-6 text-white" />}
                        color="bg-indigo-600"
                    />
                    <StatCard 
                        title="Total Documents" 
                        value={stats?.docCount || 0} 
                        icon={<Database className="w-6 h-6 text-white" />}
                        color="bg-blue-600"
                    />
                     <StatCard 
                        title="Storage Used" 
                        value={stats ? `${(stats.totalSize / 1024 / 1024).toFixed(2)} MB` : '0 MB'} 
                        icon={<HardDrive className="w-6 h-6 text-white" />}
                        color="bg-emerald-600"
                    />
                </div>

                {/* User Management */}
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700 overflow-hidden">
                    <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex flex-col md:flex-row justify-between items-center gap-4">
                        <h3 className="text-xl font-bold text-gray-900 dark:text-white">User Management</h3>
                        <div className="relative w-full md:w-64">
                            <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search users..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                            />
                        </div>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-sm text-gray-600 dark:text-gray-300">
                            <thead className="bg-gray-50 dark:bg-gray-700 text-xs uppercase font-semibold text-gray-500 dark:text-gray-400">
                                <tr className="divide-x divide-gray-200 dark:divide-gray-700">
                                    <th className="px-6 py-4">User</th>
                                    <th className="px-6 py-4">Role</th>
                                    <th className="px-6 py-4">Household</th>
                                    <th className="px-6 py-4">Joined</th>
                                    <th className="px-6 py-4 text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                                {filteredUsers.map((u) => (
                                    <tr key={u._id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition">
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-3">
                                                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-indigo-500 text-white flex items-center justify-center font-bold text-xs">
                                                    {u.first_name?.[0] || u.email[0].toUpperCase()}
                                                </div>
                                                <div>
                                                    <div className="font-medium text-gray-900 dark:text-white">
                                                        {u.first_name} {u.last_name}
                                                    </div>
                                                    <div className="text-xs text-gray-500">{u.email}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                                                u.role === 'ADMIN' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-700'
                                            }`}>
                                                {u.role}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 font-mono text-xs">{u.household_id || 'N/A'}</td>
                                        <td className="px-6 py-4">{new Date(u.created_at).toLocaleDateString()}</td>
                                        <td className="px-6 py-4 text-right">
                                            {u.role !== 'SUPER_ADMIN' && (
                                                <div className="flex justify-end gap-2">
                                                    <button 
                                                        onClick={() => handleRoleUpdate(u._id, 'ADMIN')}
                                                        className="p-1 hover:bg-green-100 text-green-600 rounded"
                                                        title="Promote to Admin"
                                                    >
                                                        <Check className="w-4 h-4" />
                                                    </button>
                                                    <button 
                                                        onClick={() => handleRoleUpdate(u._id, 'USER')}
                                                        className="p-1 hover:bg-yellow-100 text-yellow-600 rounded"
                                                        title="Demote to User"
                                                    >
                                                        <X className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
            )}

            <RegisterModal 
                isOpen={isRegisterModalOpen} 
                onClose={() => setIsRegisterModalOpen(false)} 
                onSuccess={fetchData}
            />
        </Layout>
    );
};

const StatCard = ({ title, value, icon, color }) => (
    <div className={`p-6 rounded-2xl shadow-lg flex items-center gap-4 ${color} text-white`}>
        <div className="p-3 bg-white/20 rounded-xl backdrop-blur-sm">
            {icon}
        </div>
        <div>
            <h3 className="text-2xl font-bold">{value}</h3>
            <p className="text-sm opacity-90">{title}</p>
        </div>
    </div>
);

export default AdminDashboard;
